import { isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';
import { AuthService } from '@core/auth.service';
import { PlatformService } from '@core/services/platform.service';
import { CommonService } from '@modules/shared/services/common.service';
import * as fromUserProfile from '@shared/user-profile.reducer';

/**
 * Allows a route only when the current viewer owns the site context.
 * - Not a white-label site: always allowed.
 * - White-label site: allowed only for the WLS owner; any other creator or
 *   fan is treated as a non-owner and redirected home.
 */
let deferredCheckActive = false;

const isWlsOwner = (userProfile: any, cs: CommonService): boolean => {
  if (userProfile?.is_host_owner) {
    return true;
  }
  const wlsOwnerId = cs.getWhiteLabelConfiguration()?.user?.gsi4pk;
  return !!wlsOwnerId && userProfile?.gsi4pk === wlsOwnerId;
};

export const wlsOwnerGuard: CanActivateFn = (): boolean => {
  const cs = inject(CommonService);
  const store = inject(Store<fromUserProfile.UserProfileState>);
  const authService = inject(AuthService);
  const platformService = inject(PlatformService);
  const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  if (platformService.isPrerendered) {
    return true;
  }

  if (!authService.isWhiteLabelWebsite()) {
    return true;
  }

  const userProfile = cs.getLoggedInUser();

  if (!userProfile || Array.isArray(userProfile)) {
    if (isBrowser && !deferredCheckActive) {
      deferredCheckActive = true;
      store
        .select(fromUserProfile.getUserProfileItems)
        .pipe(
          filter((profile: any) => !!profile && !Array.isArray(profile)),
          take(1)
        )
        .subscribe((profile: any) => {
          deferredCheckActive = false;
          if (!isWlsOwner(profile, cs)) {
            cs.navigateToWhiteLabelHome();
          }
        });
    }
    return true;
  }

  if (isWlsOwner(userProfile, cs)) {
    return true;
  }
  cs.navigateToWhiteLabelHome();
  return false;
};
